import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { isAdmin, isTutor, isStudent, isAssistant, isAccountant, isAdvertising } from '@/utils/roleValidation';
import { useAuth } from '@/hooks/useAuth';
import { useTutorStatus } from '@/hooks/useTutorStatus';
import { useMessageNotifications } from '@/hooks/useMessageNotifications';
import { useState, useEffect, useRef } from 'react';
import { useTutorChatNotifications } from '@/hooks/useTutorChatNotifications';
import { pb } from '@/lib/pocketbase';
import { logger } from '@/utils/secureLogger';
import { useToast } from '@/hooks/use-toast';
import { getProfilePictureUrl } from '@/utils/profileUtils';
import NotificationBadge from '@/components/NotificationBadge';
import AdminNotificationBadge from '@/components/AdminNotificationBadge';
import LiveChatNotificationBadge from '@/components/LiveChatNotificationBadge';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuBadge,
  SidebarMenuSub,
  SidebarMenuSubItem,
  SidebarMenuSubButton,
  useSidebar,
} from '@/components/ui/sidebar';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { 
  LayoutDashboard, 
  User, 
  Calendar, 
  Users, 
  BookOpen, 
  MessageCircle, 
  DollarSign, 
  Settings,
  LogOut,
  GraduationCap,
  Home,
  Clock,
  Shield,
  Headphones,
  Megaphone,
  FileText,
  BarChart2,
  Star,
  Ticket,
  UserCheck,
  UserCog,
  Loader2,
  Camera,
  Image,
  Plane,
  ChevronRight,
  Package,
  Bug,
  CreditCard,
  Receipt,
  ClipboardList
} from 'lucide-react';

interface MenuItem {
  title: string;
  url: string;
  icon: any;
  badge?: number | React.ReactNode;
  subItems?: MenuItem[];
}

const AppSidebar = () => {
  const { user, logout, isLoggingOut } = useAuth();
  const { isPendingTutor, isSuspendedTutor, hasAccessToTutorFeatures } = useTutorStatus();
  const { unreadCount } = useMessageNotifications();
  const { unreadChatCount } = useTutorChatNotifications();
  const navigate = useNavigate();
  const location = useLocation();
  const { setOpenMobile, isMobile } = useSidebar();
  const [showLiveChatSupport, setShowLiveChatSupport] = useState(false);
  const [isHeroGuide, setIsHeroGuide] = useState(false);
  const [showFitnessGuide, setShowFitnessGuide] = useState(false);
  const [showMusicGuide, setShowMusicGuide] = useState(false);
  const [showTravelGuide, setShowTravelGuide] = useState(false);
  const [localTutorDropdownOpen, setLocalTutorDropdownOpen] = useState(false);
  const [adminNotificationCounts, setAdminNotificationCounts] = useState({
    subjectRequests: 0,
    supportTickets: 0,
    tutorRequests: 0,
    totalNotifications: 0
  });
  const [userPhotoUrl, setUserPhotoUrl] = useState('');
  
  // Keep track of subscriptions for cleanup
  const subscriptionsRef = useRef<(() => void)[]>([]);

  // Fetch user photo from tutors/student_profiles collection based on role
  useEffect(() => {
    const fetchUserPhoto = async () => {
      if (!user?.id) {
        setUserPhotoUrl('');
        return;
      }

      try {
        if (user?.role === 'tutor') {
          // Fetch from tutors collection
          const tutors = await pb.collection('tutors').getList(1, 1, {
            filter: `user = "${user.id}"`
          });
          
          if (tutors.items.length > 0) {
            const tutorProfile = tutors.items[0];
            const photoUrl = getProfilePictureUrl(tutorProfile);
            setUserPhotoUrl(photoUrl);
          } else {
            setUserPhotoUrl('');
          }
        } else if (user?.role === 'student') {
          // Fetch from student_profiles collection - use 'user' field not 'userId'
          const studentProfiles = await pb.collection('student_profiles').getList(1, 1, {
            filter: `user = "${user.id}"`
          });
          
          if (studentProfiles.items.length > 0) {
            const studentProfile = studentProfiles.items[0];
            const photoUrl = getProfilePictureUrl(studentProfile);
            setUserPhotoUrl(photoUrl);
          } else {
            setUserPhotoUrl('');
          }
        } else {
          // For other roles, use avatar field
          const photoUrl = user?.avatar ? getProfilePictureUrl(user) : '';
          setUserPhotoUrl(photoUrl);
        }
      } catch (error) {
        console.error('Sidebar: Error fetching user photo:', error);
        setUserPhotoUrl('');
      }
    };

    fetchUserPhoto();
  }, [user?.id, user?.role, user?.avatar]);

  // Check if current tutor is a hero guide
  useEffect(() => {
    const checkHeroGuideStatus = async () => {
      if (user?.role === 'tutor' && hasAccessToTutorFeatures()) {
        try {
          const tutorProfile = await pb.collection('tutors').getFirstListItem(
            `user = "${user.id}"`
          );
          
          setIsHeroGuide(tutorProfile.isHeroGuide === true);
        } catch (error) {
          logger.warn('Error checking hero guide status', { error });
          setIsHeroGuide(false);
        }
      } else {
        setIsHeroGuide(false);
      }
    };

    if (user) {
      checkHeroGuideStatus();
    }
  }, [user?.id, hasAccessToTutorFeatures()]);

  // Simplified live chat agent check - show immediately for eligible tutors
  useEffect(() => {
    const checkLiveChatSupport = async () => {
      // Show live chat support for active tutors immediately
      if (user?.role === 'tutor' && hasAccessToTutorFeatures()) {
        setShowLiveChatSupport(true);
        
        // Optional: verify in background without blocking UI
        try {
          const agents = await pb.collection('liveChatAgents').getList(1, 1, {
            filter: `user = "${user.id}" && isActive = true`
          });
          
          // Update state based on actual data, but don't block initial display
          setShowLiveChatSupport(agents.items.length > 0);
        } catch (error) {
          logger.debug('Live chat agent verification failed, keeping default state');
          // Keep showing the option even if verification fails
        }
      } else {
        setShowLiveChatSupport(false);
      }
    };

    if (user) {
      checkLiveChatSupport();
    }
  }, [user?.id, hasAccessToTutorFeatures()]);

  // Fetch admin notification counts with better error handling
  const fetchAdminNotificationCounts = async () => {
    if (!user || !isAdmin(user)) return;

    try {
      logger.debug('Fetching admin notification counts');
      
      // Get pending subject requests count - using correct collection name
      let subjectRequestsCount = 0;
      try {
        const subjectRequests = await pb.collection('subject_requests').getList(1, 1, {
          filter: 'status = "pending"'
        });
        subjectRequestsCount = subjectRequests.totalItems;
        logger.debug('Subject requests found', { count: subjectRequestsCount });
      } catch (error) {
        logger.error('Error fetching subject requests', { error });
      }

      // Get pending tutor requests count
      let tutorRequestsCount = 0;
      try {
        const tutorRequests = await pb.collection('users').getList(1, 1, {
          filter: 'role = "tutor" && status = "pending"'
        });
        tutorRequestsCount = tutorRequests.totalItems;
        logger.debug('Tutor requests found', { count: tutorRequestsCount });
        } catch (error) {
          logger.error('Error fetching tutor requests', { error });
      }

      // Get unread admin notifications count
      let totalNotificationsCount = 0;
      try {
        const adminNotifications = await pb.collection('notifications').getList(1, 1, {
          filter: `userId = "${user.id}" && isRead = false`
        });
        totalNotificationsCount = adminNotifications.totalItems;
        logger.debug('Admin notifications found', { count: totalNotificationsCount });
        } catch (error) {
          logger.error('Error fetching admin notifications', { error });
      }

      // Get active support tickets count - EXCLUDE closed ones
      let supportTicketsCount = 0;
      try {
        const supportTickets = await pb.collection('liveChatSessions').getList(1, 1, {
          filter: 'status != "closed" && (status = "waiting" || status = "active")'
        });
        supportTicketsCount = supportTickets.totalItems;
        logger.debug('Support tickets found', { count: supportTicketsCount });
        } catch (error) {
          logger.error('Error fetching support tickets', { error });
      }

      const newCounts = {
        subjectRequests: subjectRequestsCount,
        supportTickets: supportTicketsCount,
        tutorRequests: tutorRequestsCount,
        totalNotifications: totalNotificationsCount
      };
      
      logger.debug('Final admin notification counts', newCounts);
      setAdminNotificationCounts(newCounts);
    } catch (error) {
      logger.error('Error in fetchAdminNotificationCounts', { error });
    }
  };

  // Setup real-time subscriptions for admin notifications
  useEffect(() => {
    if (!isAdmin(user)) return;

    // Initial fetch
    fetchAdminNotificationCounts();
    
    // Clean up existing subscriptions
    subscriptionsRef.current.forEach(unsubscribe => {
      try {
        unsubscribe();
      } catch (error) {
        logger.warn('Error cleaning up subscription', { error });
      }
    });
    subscriptionsRef.current = [];
    
    // Set up real-time subscriptions
    const setupSubscriptions = async () => {
      try {
        logger.debug('Setting up admin real-time subscriptions');
        
        // Subscribe to subject requests
        const subjectRequestsUnsub = await pb.collection('subject_requests').subscribe('*', (e) => {
          logger.debug('Subject request event received', { action: e.action });
          fetchAdminNotificationCounts();
        });
        subscriptionsRef.current.push(subjectRequestsUnsub);

        // Subscribe to user status changes (tutor requests)
        const usersUnsub = await pb.collection('users').subscribe('*', (e) => {
          logger.debug('User event received', { action: e.action });
          // Only refetch if it's a tutor status change
          if (e.record?.role === 'tutor') {
            fetchAdminNotificationCounts();
          }
        });
        subscriptionsRef.current.push(usersUnsub);

        // Subscribe to notifications
        const notificationsUnsub = await pb.collection('notifications').subscribe('*', (e) => {
          logger.debug('Notification event received', { action: e.action });
          fetchAdminNotificationCounts();
        });
        subscriptionsRef.current.push(notificationsUnsub);

        // Subscribe to live chat sessions
        const liveChatUnsub = await pb.collection('liveChatSessions').subscribe('*', (e) => {
          logger.debug('Live chat session event received', { action: e.action });
          fetchAdminNotificationCounts();
        });
        subscriptionsRef.current.push(liveChatUnsub);
        
        logger.debug('Admin subscriptions set up successfully');
      } catch (error) {
        logger.error('Error setting up admin subscriptions', { error });
      }
    };

    setupSubscriptions();

    // Cleanup function
    return () => {
      logger.debug('Cleaning up admin subscriptions');
      subscriptionsRef.current.forEach(unsubscribe => {
        try {
          unsubscribe();
        } catch (error) {
          logger.warn('Error cleaning up subscription in effect cleanup', { error });
        }
      });
      subscriptionsRef.current = [];
    };
  }, [user?.id, user?.role]);

  // Add debugging
  logger.debug('AppSidebar render', { 
    hasUser: !!user, 
    userStatus: user?.status,
    isPendingTutor,
    isSuspendedTutor,
    hasAccessToTutorFeatures: hasAccessToTutorFeatures(),
    showLiveChatSupport
  });

  const isUserAdmin = isAdmin(user);
  const isStudentUser = isStudent(user);
  const isTutorUser = isTutor(user);
  const isAssistantUser = isAssistant(user);
  const isAccountantUser = isAccountant(user);
  
  // Debug logging for assistant role
  useEffect(() => {
    if (user) {
      logger.debug('AppSidebar: User role check', {
        userId: user.id,
        role: user.role,
        isAssistantUser,
        userObject: user
      });
    }
  }, [user?.id, user?.role, isAssistantUser]);

  // Base menu items available to all users
  const baseMenuItems = [
    {
      title: "Home",
      url: "/",
      icon: Home,
    },
    {
      title: "Dashboard",
      url: isUserAdmin ? "/admin" : "/dashboard",
      icon: LayoutDashboard,
    },
    {
      title: "Profile",
      url: "/profile",
      icon: User,
    },
  ];

  // Add My Courses only for test user
  if (user?.email === 'makarsameh87@gmail.com') {
    baseMenuItems.push({
      title: "My Courses",
      url: "/my-courses",
      icon: GraduationCap,
    });
  }

  // Additional items for approved users only
  const approvedUserItems = [];

  // Student-specific items
  if (isStudentUser) {
    approvedUserItems.push(
      {
        title: "Find Tutors",
        url: "/tutors",
        icon: Users,
      },
      {
        title: "Find your local coach",
        url: "/local-fitness-coaches",
        icon: Star,
      },
      {
        title: "Schedule",
        url: "/schedule",
        icon: Calendar,
      },
      {
        title: "Lessons and Bookings",
        url: "/lessons",
        icon: BookOpen,
      },
      {
        title: "Messages",
        url: "/messages",
        icon: MessageCircle,
        badge: unreadCount > 0 ? unreadCount : undefined,
      },
      {
        title: "Payment Requests",
        url: "/payment-requests",
        icon: Receipt,
      }
    );
  }

  // Tutor-specific items (without local coach finder)
  if (isTutorUser && hasAccessToTutorFeatures()) {
    approvedUserItems.push(
      {
        title: "Find Tutors",
        url: "/tutors",
        icon: Users,
      },
      {
        title: "Schedule",
        url: "/schedule",
        icon: Calendar,
      },
      {
        title: "Lessons and Bookings",
        url: "/lessons",
        icon: BookOpen,
      },
      {
        title: "Messages",
        url: "/messages",
        icon: MessageCircle,
        badge: unreadCount > 0 ? unreadCount : undefined,
      },
      {
        title: "Payment Requests",
        url: "/payment-requests",
        icon: Receipt,
      }
    );
  }
  
  // Add Schedule and Messages for admins (but not Find Tutors or local coaches finder)
  if (isUserAdmin) {
    approvedUserItems.push(
      {
        title: "Dashboard Settings",
        url: "/dashboard",
        icon: Settings,
      },
      {
        title: "Schedule",
        url: "/schedule",
        icon: Calendar,
      },
      {
        title: "Messages",
        url: "/messages",
        icon: MessageCircle,
        badge: unreadCount > 0 ? unreadCount : undefined,
      }
    );
  }
    
  // Add tutor-specific chat and local tutor option for tutors
  if (isTutorUser && hasAccessToTutorFeatures()) {
    approvedUserItems.push({
      title: "Tutor Chats",
      url: "/tutor-chats",
      icon: MessageCircle,
      badge: unreadChatCount > 0 ? unreadChatCount : undefined,
    });
    
    // Always show local tutor option for tutors
    approvedUserItems.push({
      title: "Become Local Tutor",
      url: "/become-local-tutor",
      icon: Star,
    });
  }


  // Check if tutor has fitness subjects for local guide option
  useEffect(() => {
    const checkFitnessSubject = async () => {
      if (isTutorUser && hasAccessToTutorFeatures()) {
        try {
          const tutorProfile = await pb.collection('tutors').getFirstListItem(`user = "${user.id}"`);
          const subjects = tutorProfile.subjects || [];
          const fitnessSubjects = ['Fitness & Bodybuilding', 'Fitness', 'Bodybuilding', 'Fitness and Bodybuilding'];
          
          const hasFitness = subjects.some((subject: string) => 
            fitnessSubjects.some(fitnessSubject => 
              subject.toLowerCase().includes(fitnessSubject.toLowerCase())
            )
          );
          
          setShowFitnessGuide(hasFitness);
        } catch (error) {
          logger.warn('Error checking tutor subjects for fitness', { error });
          setShowFitnessGuide(false);
        }
      } else {
        setShowFitnessGuide(false);
      }
    };

    if (user) {
      checkFitnessSubject();
    }
  }, [user?.id, isTutorUser, hasAccessToTutorFeatures()]);

  // Check if tutor has music subjects for local guide option
  useEffect(() => {
    const checkMusicSubject = async () => {
      if (isTutorUser && hasAccessToTutorFeatures()) {
        try {
          const tutorProfile = await pb.collection('tutors').getFirstListItem(`user = "${user.id}"`);
          const subjects = tutorProfile.subjects || [];
          const musicSubjects = ['Music', 'Piano', 'Guitar', 'Violin', 'Drums', 'Singing', 'Voice', 'Music Theory', 'Music Production'];
          
          const hasMusic = subjects.some((subject: string) => 
            musicSubjects.some(musicSubject => 
              subject.toLowerCase().includes(musicSubject.toLowerCase())
            )
          );
          
          setShowMusicGuide(hasMusic);
        } catch (error) {
          logger.warn('Error checking tutor subjects for music', { error });
          setShowMusicGuide(false);
        }
      } else {
        setShowMusicGuide(false);
      }
    };

    if (user) {
      checkMusicSubject();
    }
  }, [user?.id, isTutorUser, hasAccessToTutorFeatures()]);

  // Check if tutor has travel-related subjects for travel guide option
  useEffect(() => {
    const checkTravelSubject = async () => {
      if (isTutorUser && hasAccessToTutorFeatures()) {
        try {
          const tutorProfile = await pb.collection('tutors').getFirstListItem(`user = "${user.id}"`);
          const subjects = tutorProfile.subjects || [];
          const travelSubjects = ['Travel', 'Tourism', 'Cultural Studies', 'Travel Planning', 'Geography', 'History', 'Language', 'Culture'];
          
          const hasTravel = subjects.some((subject: string) => 
            travelSubjects.some(travelSubject => 
              subject.toLowerCase().includes(travelSubject.toLowerCase())
            )
          );
          
          setShowTravelGuide(hasTravel);
        } catch (error) {
          logger.warn('Error checking tutor subjects for travel', { error });
          setShowTravelGuide(false);
        }
      } else {
        setShowTravelGuide(false);
      }
    };

    if (user) {
      checkTravelSubject();
    }
  }, [user?.id, isTutorUser, hasAccessToTutorFeatures()]);

  // Role-specific menu items
  const roleSpecificItems = [];

  if (isStudentUser) {
    roleSpecificItems.push({
      title: "My Tutors",
      url: "/my-tutors",
      icon: Users,
    });
  } else if ((isTutorUser && hasAccessToTutorFeatures()) || isUserAdmin) {
    roleSpecificItems.push({
      title: "Students",
      url: "/students",
      icon: Users,
    });
    roleSpecificItems.push({
      title: "Earnings",
      url: "/earnings",
      icon: DollarSign,
    });
    
    // Add Travel Stories for tutors - Fixed URL
    if (isTutorUser && hasAccessToTutorFeatures()) {
      roleSpecificItems.push({
        title: "Travel Stories",
        url: "/my-travel-stories",
        icon: Camera,
      });

    }
  }

  // Live Chat Support for tutors - Show for eligible tutors
  if (isTutorUser && hasAccessToTutorFeatures() && showLiveChatSupport) {
    roleSpecificItems.push({
      title: "Live Chat Support",
      url: "/live-chat-support",
      icon: Headphones,
      badge: <LiveChatNotificationBadge />,
      description: "Handle live chat requests from users"
    });
  }

  // Settings for non-students or admins
  if (!isStudentUser || isUserAdmin) {
    roleSpecificItems.push({
      title: "Settings",
      url: "/settings",
      icon: Settings,
    });
  }

  // Assistant-specific items - Check role directly as well for redundancy
  if (isAssistantUser || (user && user.role === 'assistant')) {
    logger.debug('AppSidebar: Adding assistant menu items', {
      userId: user?.id,
      isAssistantUser,
      role: user?.role
    });
    
    roleSpecificItems.push({
      title: "Payments",
      url: `/assistant/payments/${user?.id}`,
      icon: DollarSign,
    });
    
    roleSpecificItems.push({
      title: "Bookings",
      url: "/admin-bookings",
      icon: Calendar,
    });
    
    roleSpecificItems.push({
      title: "Earnings Management",
      url: "/admin/earnings-management",
      icon: Receipt,
    });
  }

  // Accountant-specific items
  if (isAccountantUser) {
    logger.debug('AppSidebar: Adding accountant dashboard button', {
      userId: user?.id,
      isAccountantUser,
      role: user?.role
    });
    
    roleSpecificItems.push({
      title: "Accountant",
      url: `/accountant`,
      icon: Receipt,
    });
    
    roleSpecificItems.push({
      title: "Earnings Management",
      url: "/admin/earnings-management",
      icon: DollarSign,
    });
  }

  // Admin-specific items with notification badges
  if (isUserAdmin) {
    roleSpecificItems.push({
      title: "Analytics",
      url: "/admin/analytics",
      icon: BarChart2,
    });
    roleSpecificItems.push({
      title: "Content",
      url: "/admin/content",
      icon: FileText,
    });
    roleSpecificItems.push({
      title: "Focus Pages",
      url: "/admin/focus-pages",
      icon: Image,
    });
    roleSpecificItems.push({
      title: "Manage Travel",
      url: "/admin/travel-management",
      icon: Plane,
    });
    roleSpecificItems.push({
      title: "Financials",
      url: "/admin/financials",
      icon: DollarSign,
    });
    roleSpecificItems.push({
      title: "Payment Dashboard",
      url: "/admin/payments",
      icon: CreditCard,
    });
    roleSpecificItems.push({
      title: "Earnings Management",
      url: "/admin/earnings-management",
      icon: Receipt,
    });
    roleSpecificItems.push({
      title: "Bookings",
      url: "/admin-bookings",
      icon: Calendar,
      badge: undefined,
    });
    roleSpecificItems.push({
      title: "User Management",
      url: "/admin/user-management",
      icon: Users,
      badge: (adminNotificationCounts.tutorRequests > 0) ? adminNotificationCounts.tutorRequests : undefined,
    });
    
    roleSpecificItems.push({
      title: "Manage Subject",
      url: "/admin/subject-management",
      icon: BookOpen,
    });
    roleSpecificItems.push({
      title: "Announcements",
      url: "/admin/announcements",
      icon: Megaphone,
    });
    roleSpecificItems.push({
      title: "Admin Live Chat",
      url: "/admin/live-chat-support",
      icon: Headphones,
      badge: <LiveChatNotificationBadge />,
    });
    roleSpecificItems.push({
      title: "Support Tickets",
      url: "/messages/support",
      icon: Ticket,
      badge: adminNotificationCounts.supportTickets > 0 ? adminNotificationCounts.supportTickets : undefined,
    });
    roleSpecificItems.push({
      title: "Local Coaches",
      url: "/admin/local-coaches",
      icon: Users,
    });
    roleSpecificItems.push({
      title: "Shop Management",
      url: "/admin/shop",
      icon: Package,
    });
    roleSpecificItems.push({
      title: "Testing & Debug",
      url: "/admin/testing",
      icon: Bug,
    });
  }

  // Tasks - accessible by all authenticated users
  if (user) {
    roleSpecificItems.push({
      title: "Tasks",
      url: "/staff/tasks",
      icon: ClipboardList,
    });
  }

  // Advertising - accessible by admin and advertising team
  if (isUserAdmin || isAdvertising(user)) {
    roleSpecificItems.push({
      title: "Advertising",
      url: "/advertising",
      icon: Megaphone,
    });
  }

  // Combine menu items based on user status
  let menuItems = [...baseMenuItems, ...approvedUserItems, ...roleSpecificItems];

  const handleNavigation = (url: string) => {
    navigate(url);
    // Close mobile sidebar after navigation
    if (isMobile) {
      setOpenMobile(false);
    }
  };

  const handleLogout = async () => {
    logger.debug('Sidebar logout clicked');
    
    if (isLoggingOut) {
      logger.debug('Logout already in progress, ignoring click');
      return;
    }
    
    // Close mobile sidebar if open
    if (isMobile) {
      setOpenMobile(false);
    }
    
    try {
      logger.debug('Calling logout function');
      await logout();
    } catch (error) {
      logger.error('Logout failed', { error });
      // Force reload even if logout fails
      window.location.href = '/';
    }
  };

  const handleLogoClick = () => {
    navigate('/');
    if (isMobile) {
      setOpenMobile(false);
    }
  };

  const isActiveRoute = (url: string) => {
    if (isUserAdmin && url === '/admin') {
      return location.pathname === '/admin' || location.pathname === '/dashboard';
    }
    return location.pathname === url;
  };

  const getUserStatusBadge = () => {
    if (isUserAdmin) {
      return <Badge className="text-xs" variant="outline">Admin</Badge>;
    }
    if (isPendingTutor) {
      return <Badge className="text-xs" variant="outline">Pending Approval</Badge>;
    }
    if (isSuspendedTutor) {
      return <Badge className="text-xs" variant="outline">Suspended</Badge>;
    }
    return null;
  };

  return (
    <Sidebar>
      <SidebarHeader className="border-b p-4">
        <div 
          className="flex items-center space-x-2 cursor-pointer hover:opacity-80 transition-opacity"
          onClick={handleLogoClick}
        >
          <div className="h-8 w-8 bg-muted rounded-lg flex items-center justify-center">
            <GraduationCap className="h-5 w-5" />
          </div>
          <span className="text-lg font-bold">TutorNesta</span>
        </div>
      </SidebarHeader>
      
      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              {menuItems.map((item) => {
                // Add specific logging for Subject Requests item
                if (item.title === "Subject Requests") {
                  logger.debug('Rendering Subject Requests item with badge', { badge: item.badge });
                }
                
                // Handle regular menu items
                return (
                  <SidebarMenuItem key={item.title}>
                    <SidebarMenuButton 
                      asChild
                      isActive={isActiveRoute(item.url)}
                    >
                      <div
                        className="w-full justify-start relative cursor-pointer flex items-center gap-2 p-2 rounded-md transition-colors hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
                        onClick={() => handleNavigation(item.url)}
                      >
                        <item.icon className="h-4 w-4" />
                        <span className="flex-1">{item.title}</span>
                        {/* Live Chat Notification Badge */}
                        {React.isValidElement(item.badge) && (
                          item.badge
                        )}
                        {item.title === "Dashboard" && isUserAdmin && (
                          <Badge className="ml-auto text-xs" variant="outline">Admin</Badge>
                        )}
                        {item.title === "Messages" && item.badge && (
                          <Badge className="ml-auto text-xs" variant="outline">
                            {item.badge > 99 ? '99+' : item.badge} New
                          </Badge>
                        )}
                        {item.title === "Manage Travel" && (
                          <Badge className="ml-auto text-xs" variant="outline">Travel</Badge>
                        )}
                        {item.title === "Local Coaches" && (
                          <Badge className="ml-auto text-xs" variant="outline">Coaches</Badge>
                        )}
                        {item.title === "Manage Subject" && (
                          <Badge className="ml-auto text-xs" variant="outline">Subject</Badge>
                        )}
                        {item.title === "User Management" && (
                          <Badge className="ml-auto text-xs" variant="outline">Users</Badge>
                        )}
                      </div>
                    </SidebarMenuButton>
                    {item.badge && item.title !== "Messages" && !React.isValidElement(item.badge) && (
                      <SidebarMenuBadge>
                        {item.badge > 99 ? '99+' : item.badge}
                      </SidebarMenuBadge>
                    )}
                  </SidebarMenuItem>
                );
              })}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {/* Quick Actions Section */}
        {(isStudentUser || hasAccessToTutorFeatures() || isUserAdmin) && (
          <SidebarGroup>
            <div className="px-2">
              <div className="flex items-center justify-between py-2">
                <span className="text-sm font-medium text-gray-700">Quick Actions</span>
                <div className="flex items-center gap-2">
                  {isUserAdmin ? <AdminNotificationBadge /> : <NotificationBadge />}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => navigate('/messages')}
                    className="relative h-8 w-8 p-0"
                  >
                    <MessageCircle className="h-4 w-4" />
                    {unreadCount > 0 && (
                      <Badge className="absolute -top-1 -right-1 h-4 w-4 rounded-full p-0 flex items-center justify-center text-xs" variant="outline">
                        {unreadCount > 9 ? '9+' : unreadCount}
                      </Badge>
                    )}
                  </Button>
                </div>
              </div>
            </div>
          </SidebarGroup>
        )}
        
        {/* Show status message for restricted tutors */}
        {(isPendingTutor || isSuspendedTutor) && (
          <SidebarGroup>
            <div className="px-4 py-3 mx-2 border rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <Clock className="h-4 w-4" />
                <span className="text-sm font-medium">
                  {isPendingTutor ? 'Pending Approval' : 'Account Suspended'}
                </span>
              </div>
              <p className="text-xs">
                {isPendingTutor 
                  ? "Your tutor application is under review. You'll gain full access once approved."
                  : "Your account has been suspended. Please contact support for assistance."
                }
              </p>
            </div>
          </SidebarGroup>
        )}
      </SidebarContent>
      
      <SidebarFooter className="border-t p-4">
        <div className="flex items-center space-x-3 mb-3">
          <Avatar className="h-8 w-8">
            <AvatarImage src={userPhotoUrl} />
            <AvatarFallback>
              {user?.name?.split(' ').map(n => n[0]).join('').toUpperCase()}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium truncate">{user?.name}</p>
            <p className="text-xs text-gray-500 truncate">{user?.email}</p>
            {getUserStatusBadge()}
          </div>
        </div>
        <Button 
          variant="outline" 
          size="sm" 
          onClick={handleLogout}
          disabled={isLoggingOut}
          className="w-full"
        >
          {isLoggingOut ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Signing out...
            </>
          ) : (
            <>
              <LogOut className="mr-2 h-4 w-4" />
              Logout
            </>
          )}
        </Button>
      </SidebarFooter>
    </Sidebar>
  );
};

export default AppSidebar;
